/**
 * Wie die Töpfe eines Tages gerechnet werden, in den drei Arten der
 * Buchhaltung: alle in einem Topf (Excel: „Trinkgeld berechnen“), alle
 * einzeln („Trinkgeld getrennt“) oder teilgebündelt — etwa ein Konzert im
 * CBE, während Yuca und Biergarten gemeinsam teilen.
 *
 * Eine Auswahl in der Kopfleiste des Tages, die sofort gilt: der Tag wird neu
 * gerechnet, die Töpfe darunter zeigen das Ergebnis, zurück geht es über
 * dieselbe Auswahl. Nur „teilgebündelt“ braucht einen Schritt mehr: welche
 * Töpfe zusammengehen, wird angekreuzt und dann übernommen — sonst rechnete
 * jedes Häkchen den Tag einmal neu. Wer wann umgestellt hat, steht im
 * Audit-Log; ein freigegebener Tag muss danach neu freigegeben werden.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type BerechnungDto, type Berechnungsart, type PoolDto } from "../lib/api";
import { ART_LABEL, beschreibung } from "../lib/berechnung";
import { gueltigeToepfe } from "../lib/topf";
import { Info } from "./ui";

export function GroupingSwitch({
  date,
  berechnung,
  locked,
}: {
  date: string;
  berechnung: BerechnungDto;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const pools = useQuery({
    queryKey: ["pools"],
    queryFn: () => api.get<{ pools: PoolDto[] }>("/pools"),
  });
  const gueltig = gueltigeToepfe(pools.data?.pools ?? [], date);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [buendel, setBuendel] = useState<string[]>([]);

  const umstellen = useMutation({
    mutationFn: (auftrag: { art: Berechnungsart; buendel?: string[] }) =>
      api.put(`/business-days/${date}/berechnung`, auftrag),
    onSuccess: () => setBearbeiten(false),
    onSettled: () => queryClient.invalidateQueries(),
  });

  // Vorbelegt mit dem Bündel, das gilt; sonst mit nichts.
  const oeffneEditor = () => {
    const namen = new Map(gueltig.map((p) => [p.name, p.id]));
    const aktuell = berechnung.art === "teil" ? (berechnung.gruppen.find((g) => g.length > 1) ?? []) : [];
    setBuendel(aktuell.map((n) => namen.get(n)).filter((id): id is string => id !== undefined));
    umstellen.reset();
    setBearbeiten(true);
  };

  const wert: Berechnungsart = bearbeiten ? "teil" : berechnung.art;
  const vorschau = gueltig
    .filter((p) => !buendel.includes(p.id))
    .map((p) => [p.name]);
  const gebuendelt = gueltig.filter((p) => buendel.includes(p.id)).map((p) => p.name);
  const buendelFehler =
    gebuendelt.length < 2
      ? "Mindestens zwei Töpfe ankreuzen."
      : gebuendelt.length === gueltig.length
        ? "Alle Töpfe zusammen ist „alle in einem Topf“."
        : null;
  // „Teilgebündelt“ braucht mindestens drei Töpfe; mit zweien gibt es nur zusammen oder getrennt.
  const arten: Berechnungsart[] = gueltig.length >= 3 ? ["alle", "einzeln", "teil"] : ["alle", "einzeln"];

  return (
    <div className="relative border-l border-line pl-4">
      <div className="label mb-1">
        Berechnung
        <Info text="Alle in einem Topf: das Trinkgeld aller Kassen wird auf die Stunden aller Bereiche verteilt. Getrennt: jeder Topf nur auf seine eigenen Leute. Teilgebündelt: einige Töpfe teilen gemeinsam, die übrigen für sich, etwa Yuca und Biergarten zusammen, während im CBE ein Konzert läuft. Gilt nur für diesen Tag und rechnet ihn sofort neu." />
      </div>
      <select
        className={`field w-auto py-1 ${berechnung.istVorgabe ? "" : "border-warn bg-warn/5"}`}
        value={wert}
        disabled={locked || umstellen.isPending}
        onChange={(e) => {
          // Fokus abgeben, damit die Pfeiltasten wieder blättern (siehe TopfWahl).
          e.currentTarget.blur();
          const art = e.target.value as Berechnungsart;
          if (art === "teil") oeffneEditor();
          else {
            setBearbeiten(false);
            umstellen.mutate({ art });
          }
        }}
        aria-label="Berechnung dieses Tages"
        title={
          locked
            ? "Der Tag ist gesperrt; seine Berechnung bleibt, wie sie ausgezahlt wurde."
            : `Gilt nur für diesen Tag; er wird sofort neu gerechnet. Vorgabe für neue Tage: ${ART_LABEL[berechnung.vorgabe.art]}, ${beschreibung(berechnung.vorgabe.gruppen)}.`
        }
      >
        {arten.map((art) => (
          <option key={art} value={art}>
            {ART_LABEL[art]}
            {/* Bei „teilgebündelt“ hängt die Vorgabe am Bündel; die nennt der Tooltip. */}
            {berechnung.vorgabe.art === art && art !== "teil" ? " (Vorgabe)" : ""}
          </option>
        ))}
      </select>
      <div className="mt-1 text-xs text-muted">
        {umstellen.isPending ? (
          "rechnet neu …"
        ) : (
          <>
            {beschreibung(berechnung.gruppen)}
            {berechnung.art === "teil" && !locked && !bearbeiten ? (
              <button type="button" className="ml-2 text-accent hover:underline" onClick={oeffneEditor}>
                ändern
              </button>
            ) : null}
          </>
        )}
      </div>
      {umstellen.error && !bearbeiten ? (
        <div className="mt-1 max-w-xs text-xs text-alert">{umstellen.error.message}</div>
      ) : null}

      {bearbeiten ? (
        <div className="absolute left-4 top-full z-30 mt-1 w-80 rounded border border-line bg-page p-4 text-sm">
          <div className="label mb-2">Diese Töpfe zusammenlegen</div>
          <div className="space-y-1.5">
            {gueltig.map((p) => (
              <label key={p.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={buendel.includes(p.id)}
                  onChange={(e) =>
                    setBuendel((alt) => (e.target.checked ? [...alt, p.id] : alt.filter((id) => id !== p.id)))
                  }
                />
                {p.name}
              </label>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted">
            Die übrigen rechnen für sich.{" "}
            {buendelFehler ?? <>So geteilt: {beschreibung([gebuendelt, ...vorschau])}</>}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={buendelFehler !== null || umstellen.isPending}
              onClick={() => umstellen.mutate({ art: "teil", buendel })}
            >
              {umstellen.isPending ? "Rechnet" : "Übernehmen und neu berechnen"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setBearbeiten(false)}>
              Abbrechen
            </button>
          </div>
          {umstellen.error ? <div className="mt-2 text-xs text-alert">{umstellen.error.message}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
